import { useState } from "react";
import api, { formatError } from "@/lib/api";
import {
  X, Loader2, Cake, MapPin, HeartHandshake, Plane, Trophy, Calendar as CalIcon,
  PartyPopper, Home, Palmtree, Coffee, Baby, Star, Repeat, Dumbbell,
} from "lucide-react";

const EVENT_TYPES = [
  { id: "birthday", label: "Birthday", icon: Cake },
  { id: "birthday_fun", label: "Birthday Fun", icon: PartyPopper },
  { id: "party", label: "Party", icon: PartyPopper },
  { id: "anniversary", label: "Anniversary", icon: HeartHandshake },
  { id: "milestone", label: "Milestone", icon: Trophy },
  { id: "trip", label: "Trip", icon: Plane },
  { id: "vacation", label: "Vacation", icon: Palmtree },
  { id: "relax", label: "Relax", icon: Coffee },
  { id: "my_home", label: "My Home", icon: Home },
  { id: "baby", label: "Baby on the way", icon: Baby },
  { id: "fundraiser", label: "Fundraiser", icon: HeartHandshake },
  { id: "workout", label: "Workout", icon: Dumbbell },
  { id: "general", label: "General", icon: CalIcon },
  { id: "pickleball", label: "Pickleball", icon: Trophy },
];

// Sub-types for the Workout flavour. Kept short and friendly so a family
// member doesn't feel like they're filling out a fitness-tracker form.
const WORKOUT_TYPES = [
  { id: "run", label: "Run" },
  { id: "walk", label: "Walk" },
  { id: "lift", label: "Lift" },
  { id: "yoga", label: "Yoga" },
  { id: "hiit", label: "HIIT" },
  { id: "stretch", label: "Stretch" },
  { id: "ride", label: "Ride" },
  { id: "swim", label: "Swim" },
  { id: "other", label: "Other" },
];

const todayStr = () => new Date().toISOString().slice(0, 10);

const blankForm = (defaultDate, defaultGroupId, groups) => ({
  groupId: defaultGroupId || groups[0]?.group_id || "",
  title: "",
  eventType: "birthday",
  date: defaultDate || todayStr(),
  time: "",
  personName: "",
  location: "",
  description: "",
  fundraiserGoal: "",
  isHighlighted: false,
  isWeHaving: true,
  recurring: false,
  workoutType: "run",
  durationMinutes: "30",
});

const hydratedForm = (ev, groups) => ({
  groupId: ev.group_id || groups[0]?.group_id || "",
  title: ev.title || "",
  eventType: ev.event_type || "birthday",
  date: ev.date || todayStr(),
  time: ev.time || "",
  personName: ev.person_name || "",
  location: ev.location || "",
  description: ev.description || "",
  fundraiserGoal: ev.fundraiser_goal != null ? String(ev.fundraiser_goal) : "",
  isHighlighted: !!ev.is_highlighted,
  isWeHaving: ev.is_we_having ?? true,
  recurring: !!ev.recurring,
  workoutType: ev.workout_type || "run",
  durationMinutes: ev.duration_minutes != null ? String(ev.duration_minutes) : "30",
});

export default function EventCreateModal({ open, onClose, groups, defaultGroupId, defaultDate, onCreated, editEvent }) {
  const isEdit = !!editEvent;
  const [form, setForm] = useState(() =>
    editEvent ? hydratedForm(editEvent, groups) : blankForm(defaultDate, defaultGroupId, groups)
  );
  const [busy, setBusy] = useState(false);
  const [err, setErr] = useState("");

  // Reset/hydrate the form whenever the modal opens OR the contextual props
  // (editEvent / defaultDate / defaultGroupId) change. This fixes the bug
  // where text fields kept the previous date's values when switching days on
  // the calendar. Uses React's "derive state from props" pattern (compare
  // session key during render) instead of a setState-in-effect.
  const sessionKey = `${open ? 1 : 0}|${editEvent?.event_id || ""}|${defaultDate || ""}|${defaultGroupId || ""}`;
  const [seenKey, setSeenKey] = useState(sessionKey);
  if (open && seenKey !== sessionKey) {
    setSeenKey(sessionKey);
    setForm(editEvent ? hydratedForm(editEvent, groups) : blankForm(defaultDate, defaultGroupId, groups));
    setErr("");
  }

  if (!open) return null;

  const setField = (k, v) => setForm((f) => ({ ...f, [k]: v }));

  const submit = async (e) => {
    e.preventDefault();
    setErr("");
    if (!form.groupId) {
      setErr("Pick a group first. You can create one from the Groups tab.");
      return;
    }
    setBusy(true);
    try {
      const recurring = form.eventType === "birthday" ? true : !!form.recurring;
      const isWorkout = form.eventType === "workout";
      const body = {
        group_id: form.groupId,
        title: form.title,
        event_type: form.eventType,
        date: form.date,
        time: form.time || null,
        person_name: form.personName || null,
        location: form.location || null,
        description: form.description,
        is_highlighted: form.isHighlighted,
        is_we_having: form.eventType === "baby" ? form.isWeHaving : false,
        fundraiser_goal:
          form.eventType === "fundraiser" && form.fundraiserGoal ? parseFloat(form.fundraiserGoal) : null,
        recurring,
        recurrence_years: recurring ? 5 : null,
        workout_type: isWorkout ? form.workoutType : null,
        duration_minutes: isWorkout && form.durationMinutes ? parseInt(form.durationMinutes, 10) : null,
      };
      let data;
      if (isEdit) {
        const patchBody = {
          title: body.title,
          event_type: body.event_type,
          date: body.date,
          time: body.time,
          person_name: body.person_name,
          location: body.location,
          description: body.description,
          is_highlighted: body.is_highlighted,
          is_we_having: body.is_we_having,
          fundraiser_goal: body.fundraiser_goal,
          recurring: body.recurring,
          recurrence_years: body.recurrence_years,
          workout_type: body.workout_type,
          duration_minutes: body.duration_minutes,
        };
        ({ data } = await api.patch(`/events/${editEvent.event_id}`, patchBody));
      } else {
        ({ data } = await api.post("/events", {
          ...body, is_public: false, likes: [], places: [],
          todos_before: [], todos_during: [], todos_after: [],
        }));
      }
      onCreated && onCreated(data);
      onClose();
    } catch (e2) {
      setErr(formatError(e2));
    } finally {
      setBusy(false);
    }
  };

  const hasLocationFields = ["party", "vacation", "trip", "relax", "my_home", "birthday_fun"].includes(form.eventType);
  const isBirthday = form.eventType === "birthday";

  return (
    <div
      className="fixed inset-0 z-50 grid place-items-center p-4"
      style={{ background: "rgba(0,0,0,0.5)" }}
      onClick={onClose}
      data-testid="event-modal"
    >
      <div
        className="bd-surface p-6 w-full max-w-xl max-h-[90vh] overflow-y-auto"
        onClick={(e) => e.stopPropagation()}
      >
        <div className="flex items-center justify-between mb-4">
          <h2 className="text-2xl font-bold" style={{ letterSpacing: "-0.03em" }}>
            {isEdit ? "Edit event" : "Add an event"}
          </h2>
          <div className="flex items-center gap-2">
            {!isEdit && (
              <button
                type="button"
                onClick={() => { setForm(blankForm(defaultDate, defaultGroupId, groups)); setErr(""); }}
                className="bd-btn bd-btn-ghost"
                style={{ padding: "0.4rem 0.7rem", fontSize: "0.75rem" }}
                data-testid="event-modal-reset"
                title="Clear all fields"
              >
                Reset
              </button>
            )}
            <button onClick={onClose} className="bd-btn bd-btn-ghost" style={{ padding: "0.5rem" }} data-testid="event-modal-close">
              <X size={16} />
            </button>
          </div>
        </div>

        {isEdit && (
          <p className="text-sm mb-3" style={{ color: "var(--text-dim)" }}>
            Update any field — backspace clears, the change saves when you tap <strong>Save changes</strong>.
          </p>
        )}

        <form onSubmit={submit} className="space-y-4">
          {!isEdit && (
            <div>
              <label className="bd-label">Group</label>
              <select
                className="bd-input"
                value={form.groupId}
                onChange={(e) => setField("groupId", e.target.value)}
                data-testid="event-group-select"
                required
              >
                {groups.length === 0 && <option value="">No groups yet</option>}
                {groups.map((g) => (
                  <option key={g.group_id} value={g.group_id}>{g.name}</option>
                ))}
              </select>
            </div>
          )}

          <div>
            <label className="bd-label flex items-center gap-2">
              Pick what kind of event
            </label>
            <div className="flex flex-wrap gap-2">
              {EVENT_TYPES.map((tt) => (
                <button
                  type="button"
                  key={tt.id}
                  onClick={() => setField("eventType", tt.id)}
                  className="bd-chip"
                  style={
                    form.eventType === tt.id
                      ? { background: "var(--primary)", color: "var(--primary-fg)", borderColor: "var(--primary)" }
                      : {}
                  }
                  data-testid={`event-type-${tt.id}`}
                >
                  <tt.icon size={12} /> {tt.label}
                </button>
              ))}
            </div>
          </div>

          {form.eventType === "baby" && (
            <div className="bd-card" style={{ padding: "0.75rem 1rem" }}>
              <label className="bd-label flex items-center gap-2">
                Who&apos;s having a baby?
              </label>
              <div className="flex gap-2">
                <button type="button" className="bd-chip"
                  style={form.isWeHaving
                    ? { background: "var(--primary)", color: "var(--primary-fg)", borderColor: "var(--primary)" } : {}}
                  onClick={() => setField("isWeHaving", true)} data-testid="baby-we">
                  We&apos;re having a baby
                </button>
                <button type="button" className="bd-chip"
                  style={!form.isWeHaving
                    ? { background: "var(--primary)", color: "var(--primary-fg)", borderColor: "var(--primary)" } : {}}
                  onClick={() => setField("isWeHaving", false)} data-testid="baby-im">
                  I&apos;m having a baby
                </button>
              </div>
            </div>
          )}

          {form.eventType === "workout" && (
            <div className="bd-card" style={{ padding: "0.75rem 1rem" }} data-testid="workout-options">
              <label className="bd-label flex items-center gap-2">
                <Dumbbell size={14} /> What kind of workout?
              </label>
              <div className="flex flex-wrap gap-2 mb-3">
                {WORKOUT_TYPES.map((wt) => (
                  <button
                    type="button"
                    key={wt.id}
                    onClick={() => setField("workoutType", wt.id)}
                    className="bd-chip"
                    style={
                      form.workoutType === wt.id
                        ? { background: "var(--primary)", color: "var(--primary-fg)", borderColor: "var(--primary)" }
                        : {}
                    }
                    data-testid={`workout-type-${wt.id}`}
                  >
                    {wt.label}
                  </button>
                ))}
              </div>
              <label className="bd-label">Duration (minutes)</label>
              <input
                className="bd-input bd-mono"
                type="number"
                min="1"
                step="1"
                value={form.durationMinutes}
                onChange={(e) => setField("durationMinutes", e.target.value)}
                placeholder="30"
                data-testid="workout-duration"
              />
              <p className="text-xs mt-2" style={{ color: "var(--text-dim)" }}>
                Tip: tap <strong>Go Live</strong> on this workout when you start — the group gets a one-tap Join In.
              </p>
            </div>
          )}

          <div>
            <label className="bd-label">Title</label>
            <input className="bd-input" required value={form.title} onChange={(e) => setField("title", e.target.value)} placeholder="Mom's 60th" data-testid="event-title" />
          </div>

          <div className="grid grid-cols-2 gap-3">
            <div>
              <label className="bd-label">{form.eventType === "baby" ? "Due date" : "Date"}</label>
              <input className="bd-input" type="date" required value={form.date} onChange={(e) => setField("date", e.target.value)} data-testid="event-date" />
            </div>
            <div>
              <label className="bd-label">Start time (optional)</label>
              <input className="bd-input" type="time" value={form.time} onChange={(e) => setField("time", e.target.value)} data-testid="event-time" />
            </div>
          </div>

          {/* Recurrence row — birthdays are forced on; everyone else gets a toggle. */}
          <div
            className="bd-card flex items-center justify-between gap-3"
            style={{ padding: "0.75rem 1rem" }}
            data-testid="event-recurrence-row"
          >
            <div className="flex items-center gap-2">
              <Repeat size={14} />
              {isBirthday ? (
                <span className="text-sm">
                  <strong>Repeats every year for 5 years.</strong> One entry, the calendar fills itself in.
                </span>
              ) : (
                <label className="text-sm flex items-center gap-2 cursor-pointer">
                  <input
                    type="checkbox"
                    checked={form.recurring}
                    onChange={(e) => setField("recurring", e.target.checked)}
                    data-testid="event-recurring"
                  />
                  Annual — repeat every year for 5 years
                </label>
              )}
            </div>
          </div>

          {hasLocationFields && (
            <div>
              <label className="bd-label"><MapPin size={12} className="inline mr-1" /> Location</label>
              <input className="bd-input" value={form.location} onChange={(e) => setField("location", e.target.value)} placeholder="Where is it?" data-testid="event-location" />
            </div>
          )}

          {!hasLocationFields && (
            <div>
              <label className="bd-label"><MapPin size={12} className="inline mr-1" /> Location (optional)</label>
              <input className="bd-input" value={form.location} onChange={(e) => setField("location", e.target.value)} placeholder="Optional" data-testid="event-location" />
            </div>
          )}

          <div>
            <label className="bd-label">Who is this for? (optional)</label>
            <input className="bd-input" value={form.personName} onChange={(e) => setField("personName", e.target.value)} placeholder="Mom" data-testid="event-person" />
          </div>

          <div>
            <label className="bd-label">Description (optional)</label>
            <textarea
              className="bd-input" rows={2}
              value={form.description}
              onChange={(e) => setField("description", e.target.value)}
              placeholder="A short note about the plan"
              data-testid="event-description"
            />
          </div>

          {form.eventType === "fundraiser" && (
            <div>
              <label className="bd-label">Fundraiser goal (USD)</label>
              <input
                className="bd-input bd-mono" type="number" min="1" step="1"
                value={form.fundraiserGoal} onChange={(e) => setField("fundraiserGoal", e.target.value)}
                placeholder="500" data-testid="event-fundraiser-goal"
              />
            </div>
          )}

          <div>
            <label className="bd-label flex items-center gap-2">
              <input
                type="checkbox"
                checked={form.isHighlighted}
                onChange={(e) => setField("isHighlighted", e.target.checked)}
                data-testid="event-highlight"
              />
              <Star size={14} /> Highlight this event for the group
            </label>
          </div>

          {err && (
            <div className="text-sm px-3 py-2 rounded" style={{ background: "color-mix(in srgb, #ff3b30 15%, transparent)", color: "#ff3b30" }} data-testid="event-error">
              {err}
            </div>
          )}

          <button className="bd-btn w-full" disabled={busy} data-testid="event-submit">
            {busy ? <Loader2 size={16} className="animate-spin" /> : (isEdit ? "Save changes" : "Add event")}
          </button>
        </form>
      </div>
    </div>
  );
}
